import { Component, type ErrorInfo, type ReactNode } from 'react';

import type { Capture } from './global-handlers';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of `children` once an error has been caught. */
  fallback: (error: unknown) => ReactNode;
  /** Deduping reporter. Called synchronously, so it claims before the global handlers. */
  capture: Capture;
}

interface ErrorBoundaryState {
  error: unknown;
  caught: boolean;
}

/**
 * Catches render errors in the provider stack. Route errors do not reach here —
 * React Router renders its own `errorElement` instead, so `router.tsx` covers
 * those separately.
 *
 * Reporting synchronously from `componentDidCatch` is what wins the dedupe
 * against the deferred global handlers and keeps `componentStack`.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: undefined, caught: false };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error, caught: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.capture(error, {
      source: 'error-boundary',
      // Trimmed: the full stack is long and the top frames are the diagnostic
      // part. Redaction happens in the reporter.
      componentStack: (info.componentStack ?? '').trim().slice(0, 2000),
    });
  }

  render(): ReactNode {
    if (this.state.caught) {
      return this.props.fallback(this.state.error);
    }
    return this.props.children;
  }
}
